/**
 * useScenarioPayload - Hook for building optimization scenario payloads
 *
 * Extracts the scenario payload building logic from ScenarioEditor.
 * Transforms form state into the format expected by the optimization solver.
 *
 * @module hooks/useScenarioPayload
 */

import { useCallback } from 'react';
import { debug } from '../utils/debug';
import { touDataToSolverFormat } from '../utils/tariffTransformers';

/**
 * Build the Site object for the scenario payload
 */
function buildSitePayload(scenarioForm, optimizationEngine, vortxSettings) {
    const siteCoords = scenarioForm.tariffLocation;

    if (!siteCoords?.lat || !siteCoords?.lon || siteCoords.lat === 0 || siteCoords.lon === 0) {
        debug.warn('[BUILD_PAYLOAD] Invalid coordinates detected:', siteCoords,
            '- this will cause backend rejection. Ensure zip code geocoding completes before submission.');
    }

    return {
        latitude: siteCoords?.lat || 0,
        longitude: siteCoords?.lon || 0,
        off_grid_flag: scenarioForm.offGrid,
        ...(optimizationEngine === 'vortx_flux' && vortxSettings.minOutageSurvivalHours ? {
            min_outage_survival_hours: parseFloat(vortxSettings.minOutageSurvivalHours),
            critical_load_fraction: parseFloat(vortxSettings.criticalLoadFraction) || 0.5,
        } : {}),
    };
}

/**
 * Build the Settings object for the scenario payload
 */
function buildSettingsPayload(scenarioForm, optimizationEngine, vortxSettings) {
    const settings = {
        zip_code: scenarioForm.zipCode || null,
    };

    if (optimizationEngine === 'vortx_flux') {
        if (vortxSettings.gridImportLimitKw && parseFloat(vortxSettings.gridImportLimitKw) > 0) {
            settings.grid_import_limit_kw = parseFloat(vortxSettings.gridImportLimitKw);
        }
        if (vortxSettings.gridExportLimitKw && parseFloat(vortxSettings.gridExportLimitKw) > 0) {
            settings.grid_export_limit_kw = parseFloat(vortxSettings.gridExportLimitKw);
        }
        if (vortxSettings.fluxPhdProfileId) {
            settings.flux_phd_profile_id = vortxSettings.fluxPhdProfileId;
        }
    }

    return settings;
}

/**
 * Build the PV object for the scenario payload
 */
function buildPVPayload(scenarioForm, budget) {
    if (!scenarioForm.usePv) return null;

    return {
        max_kw: parseFloat(scenarioForm.pvMaxKw) || 0,
        min_kw: scenarioForm.sizingMode === 'prescribed' ? parseFloat(scenarioForm.pvMaxKw) || 0 : 0,
        installed_cost_per_kw: parseFloat(scenarioForm.pvCost) || scenarioForm.DEFAULT_PV_COST_PER_KW,
        federal_itc_fraction: budget.includeItc ? (budget.itcCalculations?.effectiveItcRate || 30) / 100 : 0,
        _vortx_config: {
            mode: scenarioForm.sizingMode || 'optimal',
        },
    };
}

/**
 * Calculate battery cycle cost from warranty info
 */
function calculateCycleCost(selectedBatteryData) {
    const DEFAULT_CYCLE_COST = 0.05; // $/kWh

    if (!selectedBatteryData) return DEFAULT_CYCLE_COST;

    if (selectedBatteryData.cycle_cost_per_kwh > 0) {
        return selectedBatteryData.cycle_cost_per_kwh;
    }

    // Battery DB uses cycleLife/kwh; form state may use warranty_cycles/capacity_kwh
    const cycles = selectedBatteryData.warranty_cycles
        || selectedBatteryData.cycleLife
        || selectedBatteryData.cycle_life
        || 0;
    const capacity = selectedBatteryData.capacity_kwh
        || selectedBatteryData.kwh
        || 0;
    const cost = selectedBatteryData.equipment_cost
        || (capacity > 0 && selectedBatteryData.cost_per_kwh
            ? capacity * selectedBatteryData.cost_per_kwh
            : 0);

    if (cycles > 0 && capacity > 0 && cost > 0) {
        const costPerKwhCapacity = cost / capacity;
        return costPerKwhCapacity / cycles;
    }

    return DEFAULT_CYCLE_COST;
}

/**
 * Build the ElectricStorage object for the scenario payload
 */
function buildStoragePayload(scenarioForm, budget, selectedBatteryData, vortxSettings = {}) {
    if (!scenarioForm.useStorage) return null;

    const maxKw = parseFloat(scenarioForm.storageMaxKw) || 0;
    const maxKwh = parseFloat(scenarioForm.storageMaxKwh) || 0;
    const isPrescribed = scenarioForm.batterySizingMode === 'prescribed';
    // selectedBatteryData may be an array of batteries or a single object
    const primaryBattery = Array.isArray(selectedBatteryData) ? selectedBatteryData[0] : selectedBatteryData;
    const cycleCostPerKwh = calculateCycleCost(primaryBattery);

    // Derive max_cycles_per_year from battery warranty if user hasn't overridden
    let maxCyclesPerYear = parseFloat(vortxSettings?.batteryMaxCyclesPerYear);
    if (!maxCyclesPerYear && primaryBattery) {
        const cycleLife = primaryBattery.cycleLife || primaryBattery.cycle_life || primaryBattery.warranty_cycles || 0;
        const warrantyYears = primaryBattery.warrantyYears || primaryBattery.warranty_years || 0;
        if (cycleLife > 0 && warrantyYears > 0) {
            maxCyclesPerYear = Math.round(cycleLife / warrantyYears);
        }
    }
    if (!maxCyclesPerYear) maxCyclesPerYear = 280; // Final fallback

    const storage = {
        max_kw: maxKw,
        max_kwh: maxKwh,
        ...(isPrescribed && maxKw > 0 ? { min_kw: maxKw } : {}),
        ...(isPrescribed && maxKwh > 0 ? { min_kwh: maxKwh } : {}),
        installed_cost_per_kw: budget.capexInputs?.batteryPower?.rate || 800,
        installed_cost_per_kwh: budget.capexInputs?.battery?.rate || 400,
        soc_min_fraction: parseFloat(scenarioForm.batteryReservePercent) / 100 || 0.2,
        can_grid_charge: scenarioForm.bessChargeSource === 'pv_and_grid',
        cycle_cost_per_kwh: cycleCostPerKwh,
        _vortx_battery_config: {
            use_gen_load_bank: scenarioForm.useGenLoadBank,
            reserve_percent: parseFloat(scenarioForm.batteryReservePercent) || 20,
            max_cycles_per_year: maxCyclesPerYear,
        },
    };

    debug.log('🔋 [buildScenarioPayload] ElectricStorage:', {
        batterySizingMode: scenarioForm.batterySizingMode,
        isPrescribed,
        max_kw: maxKw,
        max_kwh: maxKwh,
        has_min_kw: isPrescribed && maxKw > 0,
        has_min_kwh: isPrescribed && maxKwh > 0,
        cycle_cost_per_kwh: cycleCostPerKwh,
        battery_has_warranty: selectedBatteryData?.warranty_cycles > 0,
    });

    return storage;
}

/**
 * Build Generator and/or CHP objects for the scenario payload
 */
function buildGeneratorPayload(scenarioForm) {
    if (!scenarioForm.useGenerator) return { Generator: null, CHP: null };

    const isPrescribed = scenarioForm.generatorSizingMode === 'prescribed';
    const uptimePct = parseFloat(scenarioForm.generatorUptimePercent) || 96.5;
    const generators = scenarioForm.generators || [];

    let Generator = null;
    let CHP = null;

    if (generators.length > 0) {
        // Multi-generator support: aggregate by fuel type
        const dieselKw = generators
            .filter(g => g.fuel_type === 'diesel')
            .reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0);
        const ngKw = generators
            .filter(g => g.fuel_type === 'natural_gas')
            .reduce((sum, g) => sum + (parseFloat(g.kw) || 0), 0);

        if (dieselKw > 0) {
            Generator = {
                max_kw: dieselKw,
                ...(isPrescribed && dieselKw > 0 ? { min_kw: dieselKw } : {}),
                fuel_cost_per_gallon: parseFloat(scenarioForm.generatorFuelCostPerGallon) || 3,
                fuel_type: 'diesel',
                _vortx_uptime_pct: uptimePct,
            };
        }

        if (ngKw > 0) {
            CHP = {
                max_kw: ngKw,
                ...(isPrescribed && ngKw > 0 ? { min_kw: ngKw } : {}),
                fuel_cost_per_mmbtu: parseFloat(scenarioForm.generatorFuelCostPerMmbtu) || 6,
                fuel_type: 'natural_gas',
                _vortx_uptime_pct: uptimePct,
            };
        }
    } else {
        // Legacy single-generator fallback
        const isNG = scenarioForm.generatorFuelType === 'natural_gas';
        const generatorKw = parseFloat(scenarioForm.generatorMaxKw) || 0;

        if (isNG) {
            CHP = {
                max_kw: generatorKw,
                ...(isPrescribed && generatorKw > 0 ? { min_kw: generatorKw } : {}),
                fuel_cost_per_mmbtu: parseFloat(scenarioForm.generatorFuelCostPerMmbtu) || 6,
                fuel_type: 'natural_gas',
                _vortx_uptime_pct: uptimePct,
            };
        } else {
            Generator = {
                max_kw: generatorKw,
                ...(isPrescribed && generatorKw > 0 ? { min_kw: generatorKw } : {}),
                fuel_cost_per_gallon: parseFloat(scenarioForm.generatorFuelCostPerGallon) || 3,
                fuel_type: 'diesel',
                _vortx_uptime_pct: uptimePct,
            };
        }
    }

    return { Generator, CHP };
}

/**
 * Build TOU tariff solver response from vortxSettings
 */
function buildTouSolverResponse(vortxSettings) {
    const { periods, windows } = vortxSettings.touData;

    const energy_ratestructure = periods.map(p => [{ rate: parseFloat(p.energy) || 0, adj: 0 }]);
    const demand_ratestructure = periods.map(p => [{ rate: parseFloat(p.demand) || 0, adj: 0 }]);

    // Schedules (12x24) - Init to 0 (Base Period)
    const weekdays = Array(12).fill(0).map(() => Array(24).fill(0));
    const weekends = Array(12).fill(0).map(() => Array(24).fill(0));

    // Map IDs to Indices
    const pIdToIndex = {};
    periods.forEach((p, idx) => pIdToIndex[p.id] = idx);

    (windows || []).forEach(w => {
        const pIdx = pIdToIndex[w.periodId];
        if (pIdx === undefined) return;

        w.months.forEach(m => {
            for (let h = w.startHour; h < w.endHour; h++) {
                if (h >= 24) continue;
                if (w.weekdays) weekdays[m][h] = pIdx;
                if (w.weekends) weekends[m][h] = pIdx;
            }
        });
    });

    const solverResponse = {
        energy_ratestructure,
        energy_weekdayschedule: weekdays,
        energy_weekendschedule: weekends,
        demand_ratestructure,
        demand_weekdayschedule: weekdays,
        demand_weekendschedule: weekends
    };

    // Add flat facility demand if specified
    if (vortxSettings.touData.facilityDemand) {
        const flatRate = parseFloat(vortxSettings.touData.facilityDemand) || 0;
        solverResponse.flatdemandstructure = [[{ rate: flatRate }]];
        solverResponse.flatdemandmonths = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    }

    // Add fixed monthly charge if specified
    if (vortxSettings.touData.fixedMonthlyFee) {
        solverResponse.fixedmonthlycharge = parseFloat(vortxSettings.touData.fixedMonthlyFee) || 0;
    }

    return solverResponse;
}

/**
 * Build the ElectricTariff object for the scenario payload
 */
function buildTariffPayload(scenarioForm, optimizationEngine, vortxSettings) {
    debug.log('🔍 [buildScenarioPayload] Tariff data check:', {
        hasCustomTariffData: !!scenarioForm.customTariffData,
        hasRateData: !!scenarioForm.customTariffData?.rate_data,
        hasTouData: !!scenarioForm.customTariffData?.rate_data?.touData,
        hasBlendedRate: scenarioForm.customTariffData?.rate_data?.blendedEnergyRate !== undefined,
    });

    // Custom tariff with rate_data
    if (scenarioForm.customTariffData?.rate_data) {
        const rd = scenarioForm.customTariffData.rate_data;
        const hasSchedules = rd.energyweekdayschedule?.length > 0 || rd.demandweekdayschedule?.length > 0;
        const hasUrdbRateStructures = hasSchedules && (rd.energyratestructure?.length > 0 || rd.demandratestructure?.length > 0);
        const hasTouData = rd.touData?.periods?.length > 0;

        if (hasUrdbRateStructures) {
            return {
                urdb_response: {
                    energyratestructure: rd.energyratestructure || [],
                    demandratestructure: rd.demandratestructure || [],
                    flatdemandstructure: rd.flatdemandstructure || [],
                    energyweekdayschedule: rd.energyweekdayschedule || [],
                    energyweekendschedule: rd.energyweekendschedule || [],
                    demandweekdayschedule: rd.demandweekdayschedule || [],
                    demandweekendschedule: rd.demandweekendschedule || [],
                    fixedmonthlycharge: rd.fixedMonthlyCharge || 0,
                    minmonthlycharge: rd.minMonthlyCharge || 0,
                    flatdemandmonths: rd.flatdemandmonths || [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
                },
                urdb_utility_name: scenarioForm.customTariffData.utility_name || '',
                urdb_rate_name: scenarioForm.customTariffData.rate_schedule_name || '',
            };
        }

        if (rd.urdb_rate_data) {
            debug.log('📊 Using pre-computed rate data');
            return {
                urdb_response: rd.urdb_rate_data,
                urdb_utility_name: scenarioForm.customTariffData.utility_name || '',
                urdb_rate_name: scenarioForm.customTariffData.rate_schedule_name || '',
            };
        }

        if (hasTouData) {
            const solverFormat = touDataToSolverFormat(rd.touData);
            debug.log('📊 Using runtime touDataToSolverFormat');
            if (solverFormat) {
                return {
                    urdb_response: solverFormat,
                    urdb_utility_name: scenarioForm.customTariffData.utility_name || '',
                    urdb_rate_name: scenarioForm.customTariffData.rate_schedule_name || '',
                };
            }
        }

        if (rd.blendedEnergyRate !== undefined && rd.blendedEnergyRate !== null) {
            debug.log('📊 Using blended rates:', rd.blendedEnergyRate);
            return {
                blended_annual_energy_rate: rd.blendedEnergyRate,
                blended_annual_demand_rate: rd.blendedDemandRate || 0,
                // Note: fixedmonthlycharge and minmonthlycharge are not supported
                // as direct keyword arguments by the Vortx Flux solver
            };
        }

        return null;
    }

    // Native 8760 tariff
    if (scenarioForm.customTariffData?.rate_mode === 'native_8760') {
        const rd = scenarioForm.customTariffData.rate_data;
        const tariff = {
            custom_electricity_rate_series_kw: rd.energy_charges,
            fixedmonthlycharge: rd.monthly_fixed_charges ? (rd.monthly_fixed_charges.reduce((a, b) => a + b, 0) / 12) : 0,
            minmonthlycharge: 0,
            urdb_utility_name: scenarioForm.customTariffData.utility_name || '',
            urdb_rate_name: scenarioForm.customTariffData.name || '',
        };

        if (optimizationEngine === 'vortx_flux') {
            tariff._vortx_native_8760 = {
                energy_charges: rd.energy_charges,
                demand_charges: rd.demand_charges,
                monthly_fixed_charges: rd.monthly_fixed_charges
            };
        }

        return tariff;
    }

    // Manual rate entry
    if (scenarioForm.useManualRate) {
        const energyRate = parseFloat(scenarioForm.blendedEnergyRate);
        const demandRate = parseFloat(scenarioForm.blendedDemandRate);
        const hasTou = vortxSettings?.rateMode === 'tou' && vortxSettings.touData?.periods;
        const hasTiered = vortxSettings?.rateMode === 'tiered' && vortxSettings.energyTiers?.length > 0;

        if (hasTou || hasTiered || (!isNaN(energyRate) && energyRate > 0)) {
            let tariff = {
                blended_annual_energy_rate: !isNaN(energyRate) && energyRate > 0 ? energyRate : null,
                blended_annual_demand_rate: !isNaN(demandRate) && demandRate >= 0 ? demandRate : 0,
            };

            if (vortxSettings) {
                if (vortxSettings.rateMode === 'tou' && vortxSettings.touData?.periods) {
                    tariff = { urdb_response: buildTouSolverResponse(vortxSettings) };
                } else if (vortxSettings.enableTieredRates && vortxSettings.energyTiers?.length > 0) {
                    tariff.energy_tiers = vortxSettings.energyTiers.map(tier => ({
                        max_usage: (!tier.max_usage || tier.max_usage.toLowerCase() === 'infinity') ? 1e9 : parseFloat(tier.max_usage),
                        rate: parseFloat(tier.rate) || 0
                    }));
                }
            }

            return tariff;
        }
    }

    return null;
}

/**
 * Apply export configuration to the scenario
 */
function applyExportConfig(scenario, scenarioForm) {
    const exportConfig = scenarioForm.exportConfig;
    if (!exportConfig?.enabled || !exportConfig?.mode) return;

    debug.log('🔄 [buildScenarioPayload] Export config:', exportConfig);

    switch (exportConfig.mode) {
        case 'none':
            if (!scenario.Settings) scenario.Settings = {};
            scenario.Settings.grid_export_limit_kw = 0;
            debug.log('🚫 [buildScenarioPayload] Export disabled');
            break;

        case 'match_import':
            if (scenario.ElectricTariff) {
                scenario.ElectricTariff.export_rate_beyond_curtailment_limit =
                    scenario.ElectricTariff.blended_annual_energy_rate || 0;
            }
            debug.log('📊 [buildScenarioPayload] Export matches import rate');
            break;

        case 'fixed':
            if (scenario.ElectricTariff) {
                scenario.ElectricTariff.export_rate_beyond_curtailment_limit =
                    parseFloat(exportConfig.rate) || 0;
            }
            debug.log(`💵 [buildScenarioPayload] Fixed export rate: $${exportConfig.rate}/kWh`);
            break;

        case 'hourly':
            if (exportConfig.tariffData?.rates && scenario.ElectricTariff) {
                scenario.ElectricTariff.export_bins = [{
                    name: 'nem3',
                    rate_per_kwh: 0.0,
                    max_kwh_per_year: -1,
                    time_varying_rates: exportConfig.tariffData.rates,
                }];
                debug.log('⏰ [buildScenarioPayload] Hourly NEM 3.0 rates');
            }
            break;

        default:
            debug.warn(`Unknown export mode: ${exportConfig.mode}`);
    }
}

/**
 * Hook for building optimization scenario payloads
 *
 * @param {Object} options
 * @param {Object} options.scenarioForm - Form state from useScenarioForm
 * @param {Object} options.loadProfile - Load profile state from useLoadProfile
 * @param {string} options.optimizationEngine - 'nrel_reopt' or 'vortx_flux'
 * @param {Object} options.vortxSettings - Vortx-specific settings
 * @param {Object} options.selectedBatteryData - Selected battery equipment data
 * @param {Object} options.budget - Budget state from useBudget
 * @returns {Function} buildScenarioPayload function
 */
export function useScenarioPayload({
    scenarioForm,
    loadProfile,
    optimizationEngine = 'vortx_flux',
    vortxSettings = {},
    selectedBatteryData = null,
    budget = {},
}) {
    const buildScenarioPayload = useCallback(() => {
        // Build base scenario
        const scenario = {
            Site: buildSitePayload(scenarioForm, optimizationEngine, vortxSettings),
            ElectricLoad: {
                loads_kw: loadProfile.loads,
                year: parseInt(loadProfile.loadYear, 10),
                critical_load_fraction: loadProfile.criticalLoadFraction || 0.5,
            },
            Financial: {
                ...scenarioForm.DEFAULT_FINANCIAL,
            },
            Settings: buildSettingsPayload(scenarioForm, optimizationEngine, vortxSettings),
        };

        // Add PV if enabled
        const pv = buildPVPayload(scenarioForm, budget);
        if (pv) scenario.PV = pv;

        // Add Storage if enabled
        const storage = buildStoragePayload(scenarioForm, budget, selectedBatteryData, vortxSettings);
        if (storage) scenario.ElectricStorage = storage;

        // Add Generator/CHP if enabled
        const { Generator, CHP } = buildGeneratorPayload(scenarioForm);
        if (Generator) scenario.Generator = Generator;
        if (CHP) scenario.CHP = CHP;

        // Add Tariff
        const tariff = buildTariffPayload(scenarioForm, optimizationEngine, vortxSettings);
        if (tariff) scenario.ElectricTariff = tariff;

        // Add CA DER charges config
        if (scenarioForm.caDerConfig?.has_onsite_generation && scenarioForm.caDerConfig?.calculated?.effective_utility) {
            scenario.ca_der_charges = scenarioForm.caDerConfig;
            debug.log('🔌 [buildScenarioPayload] Including CA DER config');
        }

        // Apply export configuration
        applyExportConfig(scenario, scenarioForm);

        return scenario;
    }, [scenarioForm, loadProfile, optimizationEngine, vortxSettings, selectedBatteryData, budget]);

    return { buildScenarioPayload };
}

export default useScenarioPayload;
